import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import PlanLayout from '../../../layout/Plan';
import ButtonPublish from '../../../components/_button/ButtonPublish';
import DialogConfirm from '../../../components/_dialog/DialogConfirm';
import ButtonDelete from '../../../components/_button/ButtonDelete';
import useNotification from '../../../components/_hook/useNotification';
import { useUnpublishPlanAdministratorsMutation } from '../../../../graphql/mutation/plan/planAdministrator/unpublishPlanAdministratorsMutation';
import ButtonUnpublish from '../../../components/_button/ButtonUnpublish';
import GraphQLTable from '../../../components/_table/GraphQLTable';
import ButtonCreate from '../../../components/_button/ButtonCreate';
import PlanAdministratorTableItem from './PlanAdministratorTableItem';
import { usePublishPlanAdministratorsMutation } from '../../../../graphql/mutation/plan/planAdministrator/publishPlanAdministratorsMutation';
import { useDeletePlanAdministratorsMutation } from '../../../../graphql/mutation/plan/planAdministrator/deletePlanAdministratorsMutation';
import FormCreatePlanAdministrator from './_module/FormCreatePlanAdministrator';
import Helmet from '../../../components/Helmet';
import { IPlanSchema } from '../../../../graphql/type/plan';
import {
  IPlanQueryData,
  IPlanVars,
  usePlanQuery,
} from '../../../../graphql/query/plan/usePlanQuery';
import KEEPITAL from '../../../components/constant/keepital';
import { useCompanyQuery } from '../../../../graphql/query/company/useCompanyQuery';
import Context from '../../../context';

function PlanAdministratorTable() {
  const { t } = useTranslation();
  const { company } = useContext(Context);
  const { notification } = useNotification();
  const [tableRefreshKey, setTableRefreshKey] = useState<number>(0);
  const [selectedItems, setSelectedItems] = useState<IPlanSchema['users'][0][]>(
    []
  );
  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false);
  const [isOpenCreateDialog, setIsOpenCreateDialog] = useState(false);
  const [
    deletePlanAdministratorsMutation,
  ] = useDeletePlanAdministratorsMutation(
    `
    updatedPlan {
      _id
    }
  `,
    {
      onCompleted: () => {
        notification.success(
          t('__item___deleted', {
            item: `${selectedItems.length} ${t('administrator', {
              count: selectedItems.length,
            })}`,
          })
        );
        setSelectedItems([]);
        setTableRefreshKey(new Date().getTime());
      },
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );
  const [
    publishPlanAdministratorsMutation,
  ] = usePublishPlanAdministratorsMutation(
    `updatedPlan {
				_id
				users {
				  id
                  status
                }
			}`,
    {
      onCompleted: () => {
        notification.success(
          t('__item___updated', {
            item: `${selectedItems.length} ${t('administrator', {
              count: selectedItems.length,
            })}`,
          })
        );
        setSelectedItems([]);
      },
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );
  const [
    unpublishPlanAdministratorsMutation,
  ] = useUnpublishPlanAdministratorsMutation(
    `updatedPlan {
				_id
				users {
				  id
                  status
                }
			}`,
    {
      onCompleted: () => {
        notification.success(
          t('__item___updated', {
            item: `${selectedItems.length} ${t('administrator', {
              count: selectedItems.length,
            })}`,
          })
        );
        setSelectedItems([]);
      },
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );

  const { data: KeepitalCompanyData } = useCompanyQuery(
    `
    _id
    teams {
      status
      id
    }
  `,
    {
      variables: { filter: { _id: KEEPITAL.ID } },
    }
  );

  function toggleDeleteDialog() {
    setIsOpenDeleteDialog(!isOpenDeleteDialog);
  }

  async function onClickPublish() {
    await publishPlanAdministratorsMutation({
      variables: {
        companyId: company._id,
        administratorIds: selectedItems.map((item) => item.id),
      },
    });
  }

  async function onClickUnpublish() {
    await unpublishPlanAdministratorsMutation({
      variables: {
        companyId: company._id,
        administratorIds: selectedItems.map((item) => item.id),
      },
    });
  }

  async function onClickDelete() {
    await deletePlanAdministratorsMutation({
      variables: {
        companyId: company._id,
        administratorIds: selectedItems.map((item) => item.id),
      },
    });
  }

  const keepitalCompany = KeepitalCompanyData
    ? KeepitalCompanyData.companies.items[0]
    : null;
  const keepitalTeams = keepitalCompany ? keepitalCompany.teams : [];

  return (
    <>
      <Helmet title={`${t('administrator')} | ${t('plan')}`} />
      <DialogConfirm
        title={t('are_you_sure_you_want_to_delete')}
        open={isOpenDeleteDialog}
        onClose={toggleDeleteDialog}
        onConfirm={onClickDelete}
      />
      <Dialog
        open={isOpenCreateDialog}
        onClose={() => setIsOpenCreateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <FormCreatePlanAdministrator
          onCancel={() => {
            setIsOpenCreateDialog(false);
          }}
          onSubmit={() => {
            setIsOpenCreateDialog(false);
            setTableRefreshKey(new Date().getTime());
          }}
        />
      </Dialog>
      <ButtonCreate
        variant="contained"
        color="primary"
        text={t('new')}
        onClick={() => setIsOpenCreateDialog(true)}
      />
      <GraphQLTable<IPlanQueryData, IPlanSchema['users'][0], IPlanVars>
        refreshKey={tableRefreshKey}
        select={`
				_id
				status 
				users {
				  id
                  status
                  superAdmin
                  userData {
                    _id
                    profile {
                      name {
                        firstname
                        lastname
                      }
                    }
                  }
                }
       	 `}
        useQuery={usePlanQuery}
        renderHeader={[
          {
            field: 'name',
            label: t('name'),
            align: 'left',
          },
          {
            field: 'action',
            label: t('action'),
            align: 'right',
          },
        ]}
        renderItem={(item) => (
          <PlanAdministratorTableItem
            item={item}
            keepitalTeams={keepitalTeams}
            setTableRefreshKey={setTableRefreshKey}
          />
        )}
        useCheckbox
        onItemSelected={setSelectedItems}
        selectedItems={selectedItems}
        defaultFilterOptions={{
          companyId: company._id,
        }}
        title={t('administrator')}
        defaultSort="_id"
        actionButtons={[
          <ButtonPublish
            key="publish"
            variant="contained"
            color="primary"
            size="small"
            text={t('publish')}
            onClick={onClickPublish}
          />,
          <ButtonUnpublish
            key="unpublish"
            color="primary"
            variant="contained"
            size="small"
            text={t('unpublish')}
            onClick={onClickUnpublish}
          />,
          <ButtonDelete
            key="delete"
            variant="contained"
            size="small"
            text={t('delete')}
            onClick={toggleDeleteDialog}
          />,
        ]}
        itemKey="id"
        useFilterDataItemCount
        filterData={(data) =>
          data.length ? ((data[0] as unknown) as IPlanSchema).users : []
        }
      />
    </>
  );
}

export default () => <PlanLayout main={<PlanAdministratorTable />} />;
